import type { FC } from 'react';
import React from 'react';
import { graphql } from 'react-relay';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { LoadableLazy, LoadablePaint } from '@confluence/loadable';

import type {
	GrantAccessDialogLoaderQuery$data,
	GrantAccessDialogLoaderQuery$variables,
} from './__generated__/GrantAccessDialogLoaderQuery.graphql.ts';
import type { GrantAccessLoaderProps } from './types.ts';

const GrantAccessDialogLoaderQuery = graphql`
	query GrantAccessDialogLoaderQuery($contentId: ID!, $accountId: ID!) {
		singleContent(id: $contentId) {
			id
			title
		}
		user: confluence_atlassianUser(id: $accountId) {
			id
			displayName
			photos {
				value
				isPrimary
			}
		}
	}
`;

export const GrantAccessDialogPaint = LoadablePaint({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-GrantAccessDialog" */ './GrantAccessDialog.tsx'))
			.GrantAccessDialog,
});

export const GrantAccessDialogLoader: FC<GrantAccessLoaderProps> = ({
	contentId,
	accountId,
	shouldSSRGrantAccessRestrictions,
	buttonPlaceholder,
	...props
}) => {
	const GrantAccessDialog = LoadableLazy({
		loader: async () =>
			(await import(/* webpackChunkName: "loadable-GrantAccessDialog" */ './GrantAccessDialog.tsx'))
				.GrantAccessDialog,
		loading: () => buttonPlaceholder,
	});

	const shouldPreloadGrantAccess = shouldSSRGrantAccessRestrictions && window.__SSR_RENDERED__;

	const { data, error } = useQueryInterop<
		GrantAccessDialogLoaderQuery$data,
		GrantAccessDialogLoaderQuery$variables
	>(GrantAccessDialogLoaderQuery, {
		variables: { contentId: contentId || '', accountId },
		skip: !contentId || !accountId,
		fetchPolicy: shouldPreloadGrantAccess ? 'cache-first' : 'cache-and-network',
	});

	let profilePhoto = '';
	const photos = data?.user?.photos;
	if (photos?.length) {
		const primary = photos.find((photo) => photo?.isPrimary);
		const foundPhoto = primary ? primary.value : photos[0]?.value;
		profilePhoto = foundPhoto || '';
	}

	const Component = shouldSSRGrantAccessRestrictions ? GrantAccessDialogPaint : GrantAccessDialog;

	if (error) {
		return <ErrorDisplay error={error} />;
	} else if (!contentId || !data?.user) {
		// If user is not recognized or we have no target contentId, render just the children.
		// User is identified via URL params and it would be easy to pass invalid data.
		// Note: must render the children, as this is how restrictions button is displayed
		return <>{props.children}</>;
	}
	return (
		<Component
			contentId={contentId}
			accountId={accountId}
			contentTitle={data?.singleContent?.title || ''}
			displayName={data?.user?.displayName || ''}
			profilePhoto={profilePhoto}
			{...props}
		/>
	);
};
